import { create } from "zustand";
import {
  createNewIndicatorId,
  createNode,
  deleteNode,
  getProject,
  listCustomIndicators,
  listFiles,
  moveNode,
  projectToRecord,
  renameNode,
  saveProjectMeta,
  saveProjectWithFiles,
  writeFile,
} from "./db";
import {
  basename,
  buildTree,
  DEFAULT_CSPROJ_PATH,
  DEFAULT_ENTRY_FILE,
  extToLanguage,
  fileId,
  getDefaultProjectFiles,
  getEntryContent,
  joinPath,
  listChildNames,
  normalizePath,
  uniqueChildName,
} from "./project-fs";
import { byteSize } from "./limits";
import {
  DEFAULT_ICON_FILE,
  iconContentToDataUrl,
  readUploadAsIconContent,
  validateIconContent,
} from "./icon";
import {
  parseCsproj,
  updateCsprojXml,
} from "./csproj";
import {
  getSdkDocContent,
  getSdkDocTitle,
} from "./sdk-docs";
import { parseIndicatorMeta, validateIndicator } from "./indicator-source";
import type { CreateProjectDraft } from "./create-draft";
import {
  defaultUserProjectOwnership,
  isProjectEditable,
} from "./project-ownership";
import {
  DEFAULT_TEMPLATE_ID,
  getTemplateById,
} from "./templates";
import type {
  CustomIndicatorProject,
  CustomIndicatorRecord,
  CustomIndicatorProjectKind,
  EditorLanguage,
  EditorTarget,
  ProjectFileNode,
  ProjectTreeNode,
  ValidationResult,
} from "./types";

const DEFAULT_FILE_TARGET: EditorTarget = {
  kind: "file",
  path: DEFAULT_ENTRY_FILE,
};

function filePathFromTarget(target: EditorTarget): string | null {
  return target.kind === "file" ? target.path : null;
}

function fileTarget(path: string): EditorTarget {
  return { kind: "file", path };
}

function activePathAfterRename(
  target: EditorTarget,
  oldPath: string,
  newPath: string
): EditorTarget {
  if (target.kind !== "file") return target;
  const active = target.path;
  if (active === oldPath) return fileTarget(newPath);
  if (active.startsWith(`${oldPath}/`)) {
    return fileTarget(`${newPath}/${active.slice(oldPath.length + 1)}`);
  }
  return target;
}

interface CustomIndicatorsStoreState {
  indicators: CustomIndicatorRecord[];
  isLoading: boolean;
  error: string | null;
  reload: () => Promise<void>;
}

export const useCustomIndicatorsStore = create<CustomIndicatorsStoreState>(
  (set) => ({
    indicators: [],
    isLoading: false,
    error: null,
    reload: async () => {
      set({ isLoading: true, error: null });
      try {
        const indicators = await listCustomIndicators();
        set({ indicators, isLoading: false });
      } catch (e) {
        set({
          isLoading: false,
          error: e instanceof Error ? e.message : "Failed to load indicators",
        });
      }
    },
  })
);

interface DirtyFile {
  content: string;
  type: "file" | "folder";
}

interface CustomIndicatorProjectStoreState {
  project: CustomIndicatorProject | null;
  files: ProjectFileNode[];
  activeEditorTarget: EditorTarget;
  dirtyFiles: Record<string, DirtyFile>;
  isNew: boolean;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;

  openNew: (draft?: string | CreateProjectDraft, legacyVersion?: string) => Promise<void>;
  openExisting: (projectId: string) => Promise<void>;
  reset: () => void;

  setActiveFile: (path: string) => void;
  openSdkDoc: (docId: string) => void;
  updateActiveFileContent: (content: string) => void;
  isViewingSdkDoc: () => boolean;
  updateProjectMeta: (
    patch: Partial<
      Pick<
        CustomIndicatorProject,
        | "name"
        | "version"
        | "description"
        | "overlay"
        | "tags"
        | "kind"
        | "iconFilePath"
        | "iconDataUrl"
      >
    >
  ) => void;
  getCsprojContent: () => string;
  getAllSourceFiles: () => Record<string, string>;
  getCompileFiles: () => Record<string, string>;
  importIconFile: (file: File) => Promise<void>;
  setIconFile: (path: string) => void;
  updateIconDataUrlFromCompile: (dataUrl?: string) => void;

  createFile: (folderPath: string, name: string) => Promise<void>;
  createFolder: (folderPath: string, name: string) => Promise<void>;
  renameFileOrFolder: (path: string, newName: string) => Promise<void>;
  moveFileOrFolder: (sourcePath: string, targetFolderPath: string) => Promise<void>;
  copyFileOrFolder: (sourcePath: string, targetFolderPath: string) => Promise<void>;
  deleteFileOrFolder: (path: string) => Promise<void>;
  setEntryFile: (path: string) => void;

  save: () => Promise<ValidationResult>;
  getEntryCode: () => string;
  getActiveContent: () => string;
  getActiveLanguage: () => EditorLanguage;
  getActiveEditorTitle: () => string;
  getTree: () => ProjectTreeNode | null;
  hasUnsavedChanges: () => boolean;
}

function getFileContent(
  files: ProjectFileNode[],
  dirtyFiles: Record<string, DirtyFile>,
  path: string
): string {
  if (dirtyFiles[path]) {
    return dirtyFiles[path].content;
  }
  const file = files.find((f) => f.path === path && f.type === "file");
  return file?.content ?? "";
}

async function resolveIconDataUrl(
  files: ProjectFileNode[],
  dirtyFiles: Record<string, DirtyFile>,
  iconFilePath?: string
): Promise<string | undefined> {
  if (!iconFilePath) return undefined;
  const content = getFileContent(files, dirtyFiles, iconFilePath);
  if (!content) return undefined;
  const validation = await validateIconContent(iconFilePath, content);
  if (!validation.ok) return undefined;
  try {
    return iconContentToDataUrl(iconFilePath, content);
  } catch {
    return undefined;
  }
}

export const useCustomIndicatorProjectStore =
  create<CustomIndicatorProjectStoreState>((set, get) => ({
    project: null,
    files: [],
    activeEditorTarget: DEFAULT_FILE_TARGET,
    dirtyFiles: {},
    isNew: false,
    isLoading: false,
    isSaving: false,
    error: null,

    openNew: async (
      draft?: string | CreateProjectDraft,
      legacyVersion = "1.0"
    ) => {
      set({ isLoading: true, error: null, dirtyFiles: {}, isNew: true });
      try {
        let name = "My Custom Indicator";
        let version = legacyVersion;
        let description = "";
        let kind: CustomIndicatorProjectKind = "indicator";
        let overlay = true;
        let templateCode: string | undefined;

        if (typeof draft === "string") {
          name = draft;
        } else if (draft) {
          name = draft.name;
          version = draft.version ?? "1.0";
          description = draft.description ?? "";
          kind = draft.kind ?? "indicator";
          const template = getTemplateById(
            draft.templateId ?? DEFAULT_TEMPLATE_ID
          );
          templateCode = template?.code;
          overlay = draft.overlay ?? template?.overlay ?? true;
        }

        const id = createNewIndicatorId();
        const now = Date.now();
        const scaffold = getDefaultProjectFiles(name, kind, {
          templateCode,
          version,
          description,
          overlay,
        });
        const files: ProjectFileNode[] = scaffold.map((item) => ({
          id: fileId(id, item.path),
          projectId: id,
          path: normalizePath(item.path),
          type: item.type,
          content: item.type === "file" ? item.content : "",
          sizeBytes: item.type === "file" ? byteSize(item.content) : 0,
          createdAt: now,
          updatedAt: now,
        }));
        const entryContent =
          files.find((f) => f.path === DEFAULT_ENTRY_FILE)?.content ?? "";
        const iconDataUrl = await resolveIconDataUrl(
          files,
          {},
          DEFAULT_ICON_FILE
        );
        const templateId =
          typeof draft === "object" && draft?.templateId
            ? draft.templateId
            : undefined;
        const project: CustomIndicatorProject = {
          id,
          name: name.trim() || "My Custom Indicator",
          version,
          description,
          overlay,
          kind,
          tags: ["custom", "csharp", "draft"],
          entryFilePath: DEFAULT_ENTRY_FILE,
          csprojFilePath: DEFAULT_CSPROJ_PATH,
          iconFilePath: DEFAULT_ICON_FILE,
          iconDataUrl,
          sizeBytes: files
            .filter((f) => f.type === "file")
            .reduce((s, f) => s + f.sizeBytes, 0),
          entryContent,
          ...defaultUserProjectOwnership(),
          sourceTemplateId: templateId,
          createdAt: now,
          updatedAt: now,
        };
        set({
          project,
          files,
          activeEditorTarget: DEFAULT_FILE_TARGET,
          isLoading: false,
        });
      } catch (e) {
        set({
          isLoading: false,
          error: e instanceof Error ? e.message : "Failed to create project",
        });
      }
    },

    openExisting: async (projectId: string) => {
      set({ isLoading: true, error: null, dirtyFiles: {}, isNew: false });
      try {
        let project = await getProject(projectId);
        if (!project) {
          throw new Error("Project not found");
        }
        if (!isProjectEditable(project)) {
          throw new Error("This indicator is read-only and cannot be edited");
        }
        project = {
          ...project,
          kind: project.kind ?? "indicator",
          csprojFilePath: project.csprojFilePath ?? DEFAULT_CSPROJ_PATH,
          iconFilePath: project.iconFilePath ?? DEFAULT_ICON_FILE,
        };
        const files = await listFiles(projectId);
        let iconDataUrl = project.iconDataUrl;
        if (!iconDataUrl && project.iconFilePath) {
          iconDataUrl = await resolveIconDataUrl(files, {}, project.iconFilePath);
        }
        if (iconDataUrl && iconDataUrl !== project.iconDataUrl) {
          project = { ...project, iconDataUrl };
        }
        const entryPath = files.some(
          (f) => f.path === project.entryFilePath && f.type === "file"
        )
          ? project.entryFilePath
          : files.find((f) => f.type === "file")?.path ?? DEFAULT_ENTRY_FILE;

        set({
          project,
          files,
          activeEditorTarget: fileTarget(entryPath),
          isLoading: false,
        });
      } catch (e) {
        set({
          isLoading: false,
          error: e instanceof Error ? e.message : "Failed to open project",
        });
      }
    },

    reset: () => {
      set({
        project: null,
        files: [],
        activeEditorTarget: DEFAULT_FILE_TARGET,
        dirtyFiles: {},
        isNew: false,
        isLoading: false,
        isSaving: false,
        error: null,
      });
    },

    setActiveFile: (path: string) => {
      const { files } = get();
      const file = files.find((f) => f.path === path);
      if (file?.type === "file") {
        set({ activeEditorTarget: fileTarget(path) });
      }
    },

    openSdkDoc: (docId: string) => {
      if (!getSdkDocContent(docId)) return;
      set({ activeEditorTarget: { kind: "sdk-doc", docId } });
    },

    isViewingSdkDoc: () => get().activeEditorTarget.kind === "sdk-doc",

    updateActiveFileContent: (content: string) => {
      const { activeEditorTarget, dirtyFiles } = get();
      const path = filePathFromTarget(activeEditorTarget);
      if (!path) return;
      set({
        dirtyFiles: {
          ...dirtyFiles,
          [path]: { content, type: "file" },
        },
      });
    },

    updateProjectMeta: (patch) => {
      const { project, files, dirtyFiles } = get();
      if (!project) return;
      const nextProject = { ...project, ...patch };
      const csprojPath = project.csprojFilePath ?? DEFAULT_CSPROJ_PATH;
      const currentCsproj = getFileContent(files, dirtyFiles, csprojPath);
      const updatedXml = updateCsprojXml(currentCsproj, {
        name: nextProject.name,
        version: nextProject.version,
        description: nextProject.description,
        overlay: nextProject.overlay,
        entryFile: nextProject.entryFilePath,
        iconFile: nextProject.iconFilePath ?? DEFAULT_ICON_FILE,
        kind: nextProject.kind,
      });
      const csprojInFiles = files.some((f) => f.path === csprojPath);
      const newDirty = { ...dirtyFiles, [csprojPath]: { content: updatedXml, type: "file" as const } };
      let newFiles = files;
      if (!csprojInFiles) {
        const now = Date.now();
        newFiles = [
          ...files,
          {
            id: fileId(project.id, csprojPath),
            projectId: project.id,
            path: csprojPath,
            type: "file" as const,
            content: updatedXml,
            sizeBytes: byteSize(updatedXml),
            createdAt: now,
            updatedAt: now,
          },
        ];
      }
      set({ project: nextProject, dirtyFiles: newDirty, files: newFiles });
    },

    getCsprojContent: () => {
      const { project, files, dirtyFiles } = get();
      if (!project) return "";
      const csprojPath = project.csprojFilePath ?? DEFAULT_CSPROJ_PATH;
      return getFileContent(files, dirtyFiles, csprojPath);
    },

    getAllSourceFiles: () => {
      const { project, files, dirtyFiles } = get();
      if (!project) return {};
      const out: Record<string, string> = {};
      for (const f of files) {
        if (f.type !== "file") continue;
        if (!f.path.endsWith(".cs")) continue;
        out[f.path] = getFileContent(files, dirtyFiles, f.path);
      }
      const csprojPath = project.csprojFilePath ?? DEFAULT_CSPROJ_PATH;
      out[csprojPath] = getFileContent(files, dirtyFiles, csprojPath);
      return out;
    },

    getCompileFiles: () => {
      const { project, files, dirtyFiles } = get();
      if (!project) return {};
      const out = get().getAllSourceFiles();
      const csprojPath = project.csprojFilePath ?? DEFAULT_CSPROJ_PATH;
      const csprojMeta = parseCsproj(getFileContent(files, dirtyFiles, csprojPath));
      const iconPath = project.iconFilePath ?? csprojMeta.iconFile;
      if (iconPath) {
        const content = getFileContent(files, dirtyFiles, iconPath);
        if (content) out[iconPath] = content;
      }
      return out;
    },

    importIconFile: async (file: File) => {
      const { project, files, dirtyFiles, isNew } = get();
      if (!project) return;

      const { path, content } = await readUploadAsIconContent(file);
      const validation = await validateIconContent(path, content);
      if (!validation.ok) {
        throw new Error(validation.error ?? "Invalid icon file");
      }

      const iconDataUrl = iconContentToDataUrl(path, content);
      const csprojPath = project.csprojFilePath ?? DEFAULT_CSPROJ_PATH;
      const updatedXml = updateCsprojXml(getFileContent(files, dirtyFiles, csprojPath), {
        iconFile: path,
      });

      const existing = files.find((f) => f.path === path && f.type === "file");
      const newDirty = {
        ...dirtyFiles,
        [path]: { content, type: "file" as const },
        [csprojPath]: { content: updatedXml, type: "file" as const },
      };

      if (isNew) {
        const now = Date.now();
        const nextFiles = existing
          ? files.map((f) =>
              f.path === path
                ? { ...f, content, sizeBytes: byteSize(content), updatedAt: now }
                : f.path === csprojPath
                  ? { ...f, content: updatedXml, sizeBytes: byteSize(updatedXml), updatedAt: now }
                  : f
            )
          : [
              ...files,
              {
                id: fileId(project.id, path),
                projectId: project.id,
                path,
                type: "file" as const,
                content,
                sizeBytes: byteSize(content),
                createdAt: now,
                updatedAt: now,
              },
            ];
        set({
          files: nextFiles,
          dirtyFiles: newDirty,
          project: {
            ...project,
            iconFilePath: path,
            iconDataUrl,
          },
          activeEditorTarget: fileTarget(path),
        });
        return;
      }

      if (existing) {
        await writeFile(project.id, path, content, "file");
      } else {
        await createNode(project.id, path, "file", content);
      }
      const refreshedFiles = await listFiles(project.id);
      set({
        files: refreshedFiles,
        dirtyFiles: newDirty,
        project: {
          ...project,
          iconFilePath: path,
          iconDataUrl,
        },
        activeEditorTarget: fileTarget(path),
      });
    },

    setIconFile: (path: string) => {
      const { project, files, dirtyFiles } = get();
      if (!project) return;
      const normalized = normalizePath(path);
      const file = files.find((f) => f.path === normalized && f.type === "file");
      if (!file) {
        throw new Error("Icon file must exist in the project");
      }
      const csprojPath = project.csprojFilePath ?? DEFAULT_CSPROJ_PATH;
      const updatedXml = updateCsprojXml(getFileContent(files, dirtyFiles, csprojPath), {
        iconFile: normalized,
      });
      void (async () => {
        const iconDataUrl = await resolveIconDataUrl(files, dirtyFiles, normalized);
        set({
          dirtyFiles: {
            ...dirtyFiles,
            [csprojPath]: { content: updatedXml, type: "file" },
          },
          project: {
            ...project,
            iconFilePath: normalized,
            iconDataUrl,
          },
        });
      })();
    },

    updateIconDataUrlFromCompile: (dataUrl?: string) => {
      const { project } = get();
      if (!project || !dataUrl) return;
      set({
        project: {
          ...project,
          iconDataUrl: dataUrl,
        },
      });
    },

    createFile: async (folderPath: string, name: string) => {
      const { project, isNew, files } = get();
      if (!project) return;
      const path = normalizePath(joinPath(folderPath, name));
      if (files.some((f) => f.path === path)) {
        throw new Error(`Path already exists: ${path}`);
      }
      if (isNew) {
        const now = Date.now();
        const node: ProjectFileNode = {
          id: fileId(project.id, path),
          projectId: project.id,
          path,
          type: "file",
          content: "",
          sizeBytes: 0,
          createdAt: now,
          updatedAt: now,
        };
        set({ files: [...files, node], activeEditorTarget: fileTarget(node.path) });
        return;
      }
      await createNode(project.id, path, "file", "");
      const updatedFiles = await listFiles(project.id);
      set({ files: updatedFiles, activeEditorTarget: fileTarget(path) });
    },

    createFolder: async (folderPath: string, name: string) => {
      const { project, isNew, files } = get();
      if (!project) return;
      const path = normalizePath(joinPath(folderPath, name));
      if (files.some((f) => f.path === path)) {
        throw new Error(`Path already exists: ${path}`);
      }
      if (isNew) {
        const now = Date.now();
        const node: ProjectFileNode = {
          id: fileId(project.id, path),
          projectId: project.id,
          path,
          type: "folder",
          content: "",
          sizeBytes: 0,
          createdAt: now,
          updatedAt: now,
        };
        set({ files: [...files, node] });
        return;
      }
      await createNode(project.id, path, "folder");
      const updatedFiles = await listFiles(project.id);
      set({ files: updatedFiles });
    },

    setEntryFile: (path: string) => {
      const { project, files, dirtyFiles } = get();
      if (!project) return;
      const normalized = normalizePath(path);
      const file = files.find((f) => f.path === normalized && f.type === "file");
      if (!file) {
        throw new Error("Entry file must be an existing file");
      }
      if (!normalized.endsWith(".cs")) {
        throw new Error("Entry file must be a .cs source file");
      }
      const content = getFileContent(files, dirtyFiles, normalized);
      set({
        project: {
          ...project,
          entryFilePath: normalized,
          entryContent: content,
        },
      });
    },

    renameFileOrFolder: async (path: string, newName: string) => {
      const { project, activeEditorTarget, dirtyFiles, files, isNew } = get();
      if (!project) return;
      const dir = path.includes("/") ? path.slice(0, path.lastIndexOf("/")) : "";
      const newPath = dir ? `${dir}/${newName}` : newName;

      if (isNew) {
        const updatedFiles = files.map((f) => {
          if (f.path === path) return { ...f, path: newPath, id: fileId(project.id, newPath) };
          if (f.path.startsWith(`${path}/`)) {
            const suffix = f.path.slice(path.length + 1);
            const moved = `${newPath}/${suffix}`;
            return { ...f, path: moved, id: fileId(project.id, moved) };
          }
          return f;
        });
        const newDirty: Record<string, DirtyFile> = {};
        for (const [p, d] of Object.entries(dirtyFiles)) {
          if (p === path) newDirty[newPath] = d;
          else if (p.startsWith(`${path}/`))
            newDirty[`${newPath}/${p.slice(path.length + 1)}`] = d;
          else newDirty[p] = d;
        }
        const newTarget = activePathAfterRename(activeEditorTarget, path, newPath);
        const entryPath =
          project.entryFilePath === path ? newPath : project.entryFilePath.startsWith(`${path}/`)
            ? `${newPath}/${project.entryFilePath.slice(path.length + 1)}`
            : project.entryFilePath;
        set({
          files: updatedFiles,
          dirtyFiles: newDirty,
          activeEditorTarget: newTarget,
          project: { ...project, entryFilePath: entryPath },
        });
        return;
      }

      await renameNode(project.id, path, newName);
      const refreshedFiles = await listFiles(project.id);

      const newDirty: Record<string, DirtyFile> = {};
      for (const [p, d] of Object.entries(dirtyFiles)) {
        if (p === path) {
          newDirty[newPath] = d;
        } else if (p.startsWith(`${path}/`)) {
          newDirty[`${newPath}/${p.slice(path.length + 1)}`] = d;
        } else {
          newDirty[p] = d;
        }
      }

      const newTarget = activePathAfterRename(activeEditorTarget, path, newPath);

      set({ files: refreshedFiles, dirtyFiles: newDirty, activeEditorTarget: newTarget });
    },

    moveFileOrFolder: async (sourcePath: string, targetFolderPath: string) => {
      const { project, activeEditorTarget, dirtyFiles, files, isNew } = get();
      if (!project) return;
      const name = sourcePath.includes("/")
        ? sourcePath.slice(sourcePath.lastIndexOf("/") + 1)
        : sourcePath;
      const newPath = targetFolderPath ? `${targetFolderPath}/${name}` : name;

      if (isNew) {
        const updatedFiles = files.map((f) => {
          if (f.path === sourcePath)
            return { ...f, path: newPath, id: fileId(project.id, newPath) };
          if (f.path.startsWith(`${sourcePath}/`)) {
            const moved = `${newPath}/${f.path.slice(sourcePath.length + 1)}`;
            return { ...f, path: moved, id: fileId(project.id, moved) };
          }
          return f;
        });
        const newDirty: Record<string, DirtyFile> = {};
        for (const [p, d] of Object.entries(dirtyFiles)) {
          if (p === sourcePath) newDirty[newPath] = d;
          else if (p.startsWith(`${sourcePath}/`))
            newDirty[`${newPath}/${p.slice(sourcePath.length + 1)}`] = d;
          else newDirty[p] = d;
        }
        const newTarget = activePathAfterRename(
          activeEditorTarget,
          sourcePath,
          newPath
        );
        const entryPath =
          project.entryFilePath === sourcePath
            ? newPath
            : project.entryFilePath.startsWith(`${sourcePath}/`)
              ? `${newPath}/${project.entryFilePath.slice(sourcePath.length + 1)}`
              : project.entryFilePath;
        set({
          files: updatedFiles,
          dirtyFiles: newDirty,
          activeEditorTarget: newTarget,
          project: { ...project, entryFilePath: entryPath },
        });
        return;
      }

      await moveNode(project.id, sourcePath, targetFolderPath);
      const refreshedFiles = await listFiles(project.id);

      const newDirty: Record<string, DirtyFile> = {};
      for (const [p, d] of Object.entries(dirtyFiles)) {
        if (p === sourcePath) {
          newDirty[newPath] = d;
        } else if (p.startsWith(`${sourcePath}/`)) {
          newDirty[`${newPath}/${p.slice(sourcePath.length + 1)}`] = d;
        } else {
          newDirty[p] = d;
        }
      }

      const newTarget = activePathAfterRename(
        activeEditorTarget,
        sourcePath,
        newPath
      );

      set({ files: refreshedFiles, dirtyFiles: newDirty, activeEditorTarget: newTarget });
    },

    copyFileOrFolder: async (sourcePath: string, targetFolderPath: string) => {
      const { project, files, dirtyFiles, isNew } = get();
      if (!project) return;

      const target = normalizePath(targetFolderPath);
      // Cannot paste a folder into itself or one of its own descendants.
      if (target === sourcePath || target.startsWith(`${sourcePath}/`)) {
        throw new Error("Cannot paste a folder into itself");
      }

      const subtree = files.filter(
        (f) => f.path === sourcePath || f.path.startsWith(`${sourcePath}/`)
      );
      if (subtree.length === 0) {
        throw new Error("Nothing to paste");
      }

      const siblingNames = listChildNames(files, target);
      const newName = uniqueChildName(siblingNames, basename(sourcePath));
      const newBasePath = normalizePath(joinPath(target, newName));
      const remap = (p: string) =>
        p === sourcePath
          ? newBasePath
          : `${newBasePath}/${p.slice(sourcePath.length + 1)}`;

      const contentFor = (f: ProjectFileNode) =>
        f.type === "file"
          ? getFileContent(files, dirtyFiles, f.path)
          : "";

      let firstFilePath = "";

      if (isNew) {
        const now = Date.now();
        const added: ProjectFileNode[] = subtree.map((f) => {
          const newPath = remap(f.path);
          const content = contentFor(f);
          if (f.type === "file" && !firstFilePath) firstFilePath = newPath;
          return {
            id: fileId(project.id, newPath),
            projectId: project.id,
            path: newPath,
            type: f.type,
            content,
            sizeBytes: f.type === "file" ? byteSize(content) : 0,
            createdAt: now,
            updatedAt: now,
          };
        });
        set({
          files: [...files, ...added],
          activeEditorTarget: firstFilePath
            ? fileTarget(firstFilePath)
            : get().activeEditorTarget,
        });
        return;
      }

      // Persisted project: create folders before their contained files.
      const ordered = [...subtree].sort((a, b) => a.path.length - b.path.length);
      for (const f of ordered) {
        const newPath = remap(f.path);
        if (f.type === "file" && !firstFilePath) firstFilePath = newPath;
        await createNode(project.id, newPath, f.type, contentFor(f));
      }
      const updatedFiles = await listFiles(project.id);
      set({
        files: updatedFiles,
        activeEditorTarget: firstFilePath
          ? fileTarget(firstFilePath)
          : get().activeEditorTarget,
      });
    },

    deleteFileOrFolder: async (path: string) => {
      const { project, activeEditorTarget, dirtyFiles, files, isNew } = get();
      if (!project) return;

      if (path === project.entryFilePath) {
        throw new Error("Cannot delete the entry indicator file");
      }

      if (isNew) {
        const updatedFiles = files.filter(
          (f) => f.path !== path && !f.path.startsWith(`${path}/`)
        );
        const newDirty = { ...dirtyFiles };
        for (const p of Object.keys(newDirty)) {
          if (p === path || p.startsWith(`${path}/`)) delete newDirty[p];
        }
        let newTarget = activeEditorTarget;
        const activePath = filePathFromTarget(activeEditorTarget);
        if (activePath === path || activePath?.startsWith(`${path}/`)) {
          const fallback =
            updatedFiles.find((f) => f.path === project.entryFilePath)?.path ??
            updatedFiles.find((f) => f.type === "file")?.path ??
            DEFAULT_ENTRY_FILE;
          newTarget = fileTarget(fallback);
        }
        set({ files: updatedFiles, dirtyFiles: newDirty, activeEditorTarget: newTarget });
        return;
      }

      await deleteNode(project.id, path);
      const updatedFiles = await listFiles(project.id);

      const newDirty = { ...dirtyFiles };
      for (const p of Object.keys(newDirty)) {
        if (p === path || p.startsWith(`${path}/`)) {
          delete newDirty[p];
        }
      }

      let newTarget = activeEditorTarget;
      const activePath = filePathFromTarget(activeEditorTarget);
      if (activePath === path || activePath?.startsWith(`${path}/`)) {
        const fallback =
          updatedFiles.find((f) => f.path === project.entryFilePath)?.path ??
          updatedFiles.find((f) => f.type === "file")?.path ??
          DEFAULT_ENTRY_FILE;
        newTarget = fileTarget(fallback);
      }

      set({
        files: updatedFiles,
        dirtyFiles: newDirty,
        activeEditorTarget: newTarget,
      });
    },

    save: async () => {
      const { project, files, dirtyFiles, isNew } = get();
      if (!project) {
        return { ok: false, errors: ["No project open"] };
      }

      set({ isSaving: true, error: null });
      try {
        const mergedFiles = files.map((f) => {
          const dirty = dirtyFiles[f.path];
          if (dirty && f.type === "file") {
            return { ...f, content: dirty.content };
          }
          return f;
        });

        const entryContent = getEntryContent(
          mergedFiles,
          project.entryFilePath
        );
        const meta = parseIndicatorMeta(entryContent);
        const csprojMeta = parseCsproj(
          getFileContent(mergedFiles, dirtyFiles, project.csprojFilePath ?? DEFAULT_CSPROJ_PATH)
        );
        const iconFilePath =
          project.iconFilePath ?? csprojMeta.iconFile ?? DEFAULT_ICON_FILE;
        const iconDataUrl = await resolveIconDataUrl(
          mergedFiles,
          dirtyFiles,
          iconFilePath
        );
        const validation = validateIndicator(entryContent);
        if (!validation.ok) {
          set({ isSaving: false, error: validation.errors[0] });
          return validation;
        }
        const updatedProject: CustomIndicatorProject = {
          ...project,
          name: project.name.trim() || meta.name || csprojMeta.name || "Custom Indicator",
          version: project.version.trim() || meta.version || csprojMeta.version || "1.0",
          overlay: project.overlay ?? meta.overlay ?? csprojMeta.overlay ?? true,
          kind: project.kind ?? csprojMeta.kind ?? "indicator",
          iconFilePath,
          iconDataUrl,
          entryContent,
          updatedAt: Date.now(),
        };

        if (isNew) {
          const fileUpdates = mergedFiles.map((f) => ({
            path: f.path,
            content: f.content,
            type: f.type,
          }));
          updatedProject.sizeBytes = mergedFiles
            .filter((f) => f.type === "file")
            .reduce((s, f) => s + byteSize(f.content), 0);
          await saveProjectWithFiles(updatedProject, fileUpdates);
        } else {
          const fileUpdates = Object.entries(dirtyFiles).map(([path, d]) => ({
            path,
            content: d.content,
            type: d.type,
          }));

          updatedProject.entryContent = entryContent;
          if (fileUpdates.length > 0) {
            await saveProjectWithFiles(updatedProject, fileUpdates);
          } else {
            await saveProjectMeta(updatedProject);
          }
        }

        const refreshedFiles = await listFiles(project.id);
        const refreshedProject = {
          ...updatedProject,
          sizeBytes: refreshedFiles
            .filter((f) => f.type === "file")
            .reduce((s, f) => s + f.sizeBytes, 0),
        };

        set({
          project: refreshedProject,
          files: refreshedFiles,
          dirtyFiles: {},
          isNew: false,
          isSaving: false,
        });

        return validation;
      } catch (e) {
        const message = e instanceof Error ? e.message : "Save failed";
        set({ isSaving: false, error: message });
        return { ok: false, errors: [message] };
      }
    },

    getEntryCode: () => {
      const { project, files, dirtyFiles } = get();
      if (!project) return "";
      const merged = files.map((f) => {
        const dirty = dirtyFiles[f.path];
        return dirty && f.type === "file" ? { ...f, content: dirty.content } : f;
      });
      return getEntryContent(merged, project.entryFilePath);
    },

    getActiveContent: () => {
      const { activeEditorTarget, files, dirtyFiles } = get();
      if (activeEditorTarget.kind === "sdk-doc") {
        return getSdkDocContent(activeEditorTarget.docId) ?? "";
      }
      return getFileContent(files, dirtyFiles, activeEditorTarget.path);
    },

    getActiveLanguage: () => {
      const { activeEditorTarget } = get();
      if (activeEditorTarget.kind === "sdk-doc") {
        return "markdown";
      }
      return extToLanguage(activeEditorTarget.path);
    },

    getActiveEditorTitle: () => {
      const { activeEditorTarget } = get();
      if (activeEditorTarget.kind === "sdk-doc") {
        return getSdkDocTitle(activeEditorTarget.docId);
      }
      return basename(activeEditorTarget.path);
    },

    getTree: () => {
      const { project, files } = get();
      if (!project) return null;
      return buildTree(files, project.name);
    },

    hasUnsavedChanges: () => {
      const { dirtyFiles, project } = get();
      if (!project) return false;
      return Object.keys(dirtyFiles).length > 0;
    },
  }));

export { projectToRecord };
